// ============================================================================
// FILE:    src/app/core/auth/permission-role.map.ts
// VERSION: v2.0.0 — RBAC Phase 2
// DATE:    2026-05-09
//
// ZWECK:
//   Legacy-Brücke: numerischer perm-Wert → Role-Enum.
//   Wird vom CapabilityService als FALLBACK verwendet wenn kein role_key
//   aus Redis vorliegt (z.B. ältere Access-Einträge ohne role_key).
//
//   PRIMÄRER PFAD (v2.0.0+):
//     Redis slot.role_key  →  roleKeyToRole()  →  Role-Enum
//
//   FALLBACK-PFAD (Legacy / Migration):
//     access.permission  →  projectPermToRole()  →  Role-Enum
//
// ORG-PERMISSION MAPPING:
//   orgPerm 0 → NONE
//   orgPerm 1 → APP_USER        (normaler User, benötigt CAL)
//   orgPerm 3 → PMO_OFFICER     (App-Management komplett, benötigt CAL)
//   orgPerm 7 → SUPER_ADMIN     (Fallback, kein CAL-Check, bypassed alles)
//
//   HINWEIS: Werte 5 und 6 wurden in v2.0.0 zu 3 (PMO_OFFICER) zusammengelegt.
//   Falls die DB noch Einträge mit permission=5 oder permission=6 hat:
//   diese werden auf PMO_OFFICER gemappt (>= 3 Bedingung greift).
//   DB-Migration ist empfohlen aber nicht zwingend für Funktionalität.
//
// PROJEKT-PERMISSION MAPPING:
//   perm 0 → PROJECT_IDLE    (kein Zugang, access_state prüfen)
//   perm 1 → PROJECT_MEMBER  (Fallback: ohne role_key = Member, nicht Viewer)
//   perm 3 → PROJECT_IT_PM
//   perm 5 → PROJECT_ADMIN
//
//   HINWEIS: PROJECT_VIEWER hat perm=1 — Unterschied zu PROJECT_MEMBER nur
//   über role_key erkennbar. Deshalb: ohne role_key → Member als sicherer
//   Default (Member kann mehr als Viewer; Viewer ist bewusst restriktiv).
// ============================================================================

import { Role } from './roles.enum';

/**
 * Mappt numerischen org_access_permission-Wert auf Role-Enum.
 * Quelle: ciamreg.permission → Redis org_access_permission.
 */
export function orgPermToRole(perm: number): Role {
  if (perm >= 7) return Role.SUPER_ADMIN;
  if (perm >= 3) return Role.PMO_OFFICER;   // umfasst Legacy 5 und 6
  if (perm >= 1) return Role.APP_USER;
  return Role.NONE;
}

/**
 * Mappt numerischen Projekt-perm-Wert auf Role-Enum.
 * Fallback wenn kein role_key aus Redis vorliegt.
 * Quelle: access.permission → Redis slot.perm.
 *
 * WICHTIG: perm=1 → PROJECT_MEMBER (nicht VIEWER).
 * Viewer ist bewusst restriktiver und wird nur via role_key gesetzt.
 * Ein User ohne role_key bekommt die Member-Capabilities als sicheren Default.
 */
export function projectPermToRole(perm: number): Role {
  if (perm >= 5) return Role.PROJECT_ADMIN;
  if (perm >= 3) return Role.PROJECT_IT_PM;
  if (perm >= 1) return Role.PROJECT_MEMBER;
  return Role.PROJECT_IDLE;
}

/**
 * Mappt role_key-String (aus Redis slot.role_key) auf Role-Enum.
 * Dies ist der PRIMÄRE Pfad ab v2.0.0 (reg.route.js schreibt role_key in Redis).
 *
 * Unbekannte role_keys → PROJECT_MEMBER als sicherer Fallback
 * (Fehler bei neuem role_key der noch nicht gemappt ist → Member ist besser
 * als IDLE, da der User ja approved ist).
 */
export function roleKeyToRole(roleKey: string | null | undefined): Role | null {
  if (!roleKey) return null;  // null = Fallback auf perm-Mapping

  switch (roleKey) {
    case 'project_idle':             return Role.PROJECT_IDLE;
    case 'project_viewer':           return Role.PROJECT_VIEWER;
    case 'project_member':           return Role.PROJECT_MEMBER;
    case 'project_planner':          return Role.PROJECT_PLANNER;
    case 'project_solution_author':  return Role.PROJECT_SOLUTION_AUTHOR;
    case 'project_it_pm':            return Role.PROJECT_IT_PM;   // Legacy-Komposit (Planner+Solution)
    case 'project_admin':            return Role.PROJECT_ADMIN;

    // Legacy role_keys aus dem alten getUserRole.js (Backend)
    case 'Pending':   return Role.PROJECT_IDLE;
    case 'Maker':     return Role.PROJECT_MEMBER;
    case 'Manager':   return Role.PROJECT_IT_PM;
    case 'Admin':     return Role.PROJECT_ADMIN;

    default:
      // Unbekannter role_key → Member als sicherer Default
      console.warn(`[roleKeyToRole] Unknown role_key: "${roleKey}" → fallback to PROJECT_MEMBER`);
      return Role.PROJECT_MEMBER;
  }
}


